import { FREE_TOOL_REPORT_TTL_DAYS } from '@/lib/free-tools/catalog'
import {
  FREE_EMAIL_AUDIT_ADDRESS,
  FREE_EMAIL_AUDIT_MAX_HTML_BYTES,
} from '@/lib/free-tools/email-audit/contracts'
import { PREVIEW_CLIENTS } from '@/lib/mailgun/inspect/preview-clients'

/**
 * Every line of copy on `/email-audit` and its report pages. Plain strings
 * only, so the page, the client island and the markdown twin share them.
 */

const MAX_HTML_MB = Math.round(FREE_EMAIL_AUDIT_MAX_HTML_BYTES / 1024 / 1024)
const REPORT_DAYS = `${FREE_TOOL_REPORT_TTL_DAYS} days`

/** How many inboxes the signed-in rendering preview covers. */
export const RENDERING_CLIENT_COUNT = PREVIEW_CLIENTS.length

export const EMAIL_AUDIT_HERO = {
  title: 'Free email',
  accent: 'audit',
  description:
    'Paste your HTML, upload a file or forward an email. Brew checks links, images, accessibility, client support and copy in seconds.',
} as const

export const EMAIL_AUDIT_TABS = [
  { id: 'paste', label: 'Paste HTML' },
  { id: 'upload', label: 'Upload file' },
  { id: 'email', label: 'Send by email' },
] as const

export type EmailAuditTabId = (typeof EMAIL_AUDIT_TABS)[number]['id']

export const EMAIL_AUDIT_FORM = {
  htmlLabel: 'Email HTML',
  htmlPlaceholder: 'Paste the full HTML of your email, from <html> to </html>',
  subjectLabel: 'Subject line',
  subjectPlaceholder: 'Optional, so the subject is checked too',
  previewLabel: 'Preview text',
  previewPlaceholder: 'Optional, read from the email when blank',
  sample: 'Try a sample email',
  sampleShort: 'Sample',
  envelopeToggle: 'Add the subject line and preview text (optional)',
  submit: 'Audit my email',
  sizeHint: `Up to ${MAX_HTML_MB} MB of HTML`,
  dropTitle: 'Drop an .html file here',
  dropBrowse: 'or browse your computer',
  dropReplace: 'Choose another file',
  emptyHtml: 'Paste or upload the email HTML first.',
  fileRejected: 'Choose an .html or .htm file.',
  fileTooLarge: `That file is over ${MAX_HTML_MB} MB. Gmail clips anything past 102 KB.`,
  fileUnreadable: 'That file could not be read. Choose it again.',
  sampleFailed: 'The sample email could not load. Try again.',
} as const

export const EMAIL_AUDIT_BY_EMAIL = {
  address: FREE_EMAIL_AUDIT_ADDRESS,
  copy: 'Copy address',
  copied: 'Copied',
  steps: [
    'Send a test from your email platform to this address, or forward any email you received.',
    'Brew replies within a minute with your score, the top fixes and a link to the full report.',
  ],
  note: 'A test send is the most accurate: forwarding lets your mail app rewrite the HTML. Replies go only to senders whose domain passes DKIM or DMARC.',
} as const

/** Shown while the audit runs: one line per check, in the order they settle. */
export const EMAIL_AUDIT_PROGRESS = [
  'Reading the HTML and the rules',
  'Opening every link',
  'Fetching and weighing images',
  'Checking support in major email clients',
  'Reviewing subject, preview and link text',
  'Running accessibility checks',
] as const

/** The free report's verdict, read with the score beside it. */
export const EMAIL_AUDIT_VERDICT_COPY = {
  ready: 'Ready to send',
  needs_review: 'Worth a second look',
  not_ready: 'Fix before sending',
} as const

export const EMAIL_AUDIT_REPORT_COPY = {
  kicker: 'Email audit',
  scoreLabel: 'Score',
  fullMode: 'Full audit',
  quickMode: 'Instant checks',
  quickModeNote:
    'The full audit was busy, so this ran the instant checks. Run it again in a minute for live links, images and accessibility.',
  partialScoreNote:
    'Some checks could not run, so the score covers the checks that did.',
  forwardedNote:
    'Forwarding rewrites HTML. For an exact result, send a test from your email platform instead.',
  prompt: 'Copy prompt',
  promptCopied: 'Copied',
  promptCleanEmail: 'Copy report',
  categoryClean: 'No issues',
  categoryUnchecked: 'Not confirmed this time',
  categoryIssues: (count: number) =>
    `${count} ${count === 1 ? 'issue' : 'issues'}`,
  sizeDetail: (size: string) => `${size} of HTML · Gmail clips at 102 KB`,
  findingsTitle: 'What to fix',
  railTitle: (count: number) => `${count} to fix`,
  railHint: 'Pick one to find it in your email',
  passedTitle: 'Passed',
  uncheckedTitle: 'Not checked this time',
  dock: (count: number) =>
    `Hand ${count === 1 ? 'this fix' : `all ${count} fixes`} to your coding agent in one paste.`,
  dockClean: 'Nothing to fix. Keep this report handy for your agent.',
  showInEmail: 'Show in email',
  noFindings: 'Nothing to fix. This email is ready to send.',
  noFindingsUnverified: 'No issues found in the checks that ran.',
  findingsTruncatedNote: (total: number) =>
    `Only some of this email's ${total} findings are listed, blockers first. Fix these and audit again to see the rest.`,
  occurrences: (count: number) => `${count} places`,
  previewTitle: 'Your email',
  previewDesktop: 'Desktop',
  previewMobile: 'Mobile',
  previewHint: 'Hover an issue to find it in the email.',
  share: 'Share',
  shared: 'Link copied',
  again: 'Audit another',
  audited: (date: string) => `Audited ${date}`,
  expires: (date: string) => `Link works until ${date}`,
} as const

/** The last line of the reply to an email sent to the audit address. */
export const EMAIL_AUDIT_REPLY_FOOTER = `You emailed ${FREE_EMAIL_AUDIT_ADDRESS}, so Brew replied with your audit. The report link works for ${REPORT_DAYS}.`

/** The shared report page (`/email-audit/r/<id>`). */
export const EMAIL_AUDIT_SHARED_REPORT = {
  title: 'Email audit',
  accent: 'report',
  description:
    'A free audit of one email: links, images, accessibility, client support and copy. Run your own in seconds.',
  cta: 'Audit your own email',
  missingTitle: 'This report has expired',
  missingBody: `Shared reports last ${REPORT_DAYS} and are then deleted, along with everything in them.`,
  unavailableTitle: 'This report could not load',
  unavailableBody:
    'Brew could not reach its report store. Refresh in a moment.',
} as const

/** The signed-in step the page sells: rendering across real inboxes. */
export const EMAIL_AUDIT_RENDERING = {
  title: 'Will it look right in every inbox?',
  description: `Code that passes can still break in Outlook or dark mode. Log in to see screenshots of this email in ${RENDERING_CLIENT_COUNT} inboxes and devices.`,
  lockedTitle: `See it in ${RENDERING_CLIENT_COUNT} inboxes`,
  lockedBody:
    'Real screenshots from Gmail, Outlook, Apple Mail and Yahoo, on desktop, phone and dark mode.',
  ctaWithEmail: 'Preview this email in every inbox',
  ctaWithoutEmail: 'Log in to preview across inboxes',
  fixCta: 'Fix these issues with Brew',
  /** The report's compact pair under the copy-prompt button. */
  reportPreview: `Preview in ${RENDERING_CLIENT_COUNT} inboxes`,
  reportFix: 'Fix with Brew',
  /** The prompt the email arrives with in Brew's chat after sign-up. */
  renderingPrompt:
    'Import this email, then preview how it renders across email clients and devices.',
  fixPrompt:
    'Import this email and fix the issues the free audit found, then audit it again.',
  blockedBody:
    "Your browser blocked saving this email, so Brew can't bring it in after you log in. Download it, then attach it in Brew's chat.",
  blockedDownload: 'Download the email',
  blockedContinue: 'Log in without it',
} as const

export const EMAIL_AUDIT_CHECKS_SECTION = {
  title: 'What the audit checks',
  description:
    'The same checks Brew runs before every send, from live links to how screen readers hear your email.',
} as const

export const EMAIL_AUDIT_WAYS_SECTION = {
  title: 'Three ways to audit',
  description: 'Use whichever fits where your email lives. No account needed.',
  steps: [
    {
      step: '01',
      title: 'Paste HTML',
      body: 'Copy the code from your email platform or builder and paste it in.',
    },
    {
      step: '02',
      title: 'Upload a file',
      body: 'Drop an exported .html file. Nothing leaves your browser until you run the audit.',
    },
    {
      step: '03',
      title: 'Send by email',
      body: `Send a test or forward an email to ${FREE_EMAIL_AUDIT_ADDRESS} and get the report back by reply.`,
    },
  ],
} as const

export type EmailAuditFaqItem = { id: string; question: string; answer: string }

export const EMAIL_AUDIT_FAQ: ReadonlyArray<EmailAuditFaqItem> = [
  {
    id: 'free',
    question: 'Is the email audit really free?',
    answer:
      'Yes. Run as many audits as you like, one per second, with no account. Logging in adds rendering previews, fixes in chat and audits on every send.',
  },
  {
    id: 'stored',
    question: 'Do you keep my email?',
    answer: `No. Brew never stores the HTML. It keeps only the report (the findings and the numbers) so its share link works for ${REPORT_DAYS}, then deletes it.`,
  },
  {
    id: 'links',
    question: 'Will checking my links count as clicks?',
    answer:
      'The audit opens each link from Brew’s servers to see where it lands, the way a link checker does. Tracked links may log that visit, and one-time links such as password resets can be used up, so audit a test copy of those.',
  },
  {
    id: 'score',
    question: 'How is the score calculated?',
    answer:
      'Each issue lowers the score by its severity: 20 for critical, 10 for an error and 3 for a warning, counted up to three times per rule. The verdict comes first: any error means the email is not ready, whatever the score.',
  },
  {
    id: 'forward',
    question: 'I emailed the audit address and got no reply. Why?',
    answer:
      'Brew only replies to senders whose domain passes DKIM or DMARC, so nobody can use the address to email someone else. Paste or upload the HTML instead, or send from an authenticated domain.',
  },
  {
    id: 'rendering',
    question: 'Does the audit show how my email looks in Outlook?',
    answer: `It flags the HTML and CSS that Outlook, Gmail and other clients drop. To see the email itself, log in and preview it in ${RENDERING_CLIENT_COUNT} real inboxes and devices.`,
  },
]
